import React, { useState, useEffect, useRef } from "react";
import { NavLink, Link } from "react-router-dom";
import { ASSETS } from "@/data/content";
import { openBookingModal } from "@/components/BookingModal";
import { useI18n } from "@/i18n";
import { LangToggle } from "@/components/LangToggle";
import { Menu, X } from "lucide-react";

export default function Nav() {
  const { t } = useI18n();
  const NAV_PAGES = t.nav.pages;
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      if (y > 160 && y > lastY.current + 8) {
        setHidden(true);
      } else if (y < lastY.current - 4) {
        setHidden(false);
      }
      lastY.current = y;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navigation-menu"
      className={`fixed top-0 left-0 right-0 z-50 bg-brand-red text-canvas border-b border-white/10 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.25)] transition-transform duration-700 ease-out ${
        hidden ? "-translate-y-full" : "translate-y-0"
      }`}
    >
      <div className="max-w-[1700px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between gap-6">
        <Link
          to="/"
          data-testid="nav-logo"
          aria-label="PrzyStań w Ciszy — strona główna"
          className="flex items-center gap-3 group shrink-0"
        >
          <img
            src={ASSETS.logo}
            alt="PrzyStań w Ciszy — logo z żurawiami"
            className="h-12 w-12 md:h-16 md:w-16 object-contain group-hover:scale-105 transition-transform duration-500"
          />
          <span className="serif text-2xl md:text-3xl font-light tracking-tight text-canvas whitespace-nowrap group-hover:text-canvas/85 transition-colors duration-500 hidden 2xl:inline">
            PrzyStań w Ciszy
          </span>
        </Link>

        <nav className="hidden lg:flex flex-1 items-center justify-center gap-5 xl:gap-8">
          {NAV_PAGES.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.to === "/" ? "start" : l.to.slice(1)}`}
              className={({ isActive }) =>
                `text-xs xl:text-[13px] font-medium tracking-[0.1em] uppercase transition-colors duration-500 relative whitespace-nowrap after:absolute after:left-0 after:-bottom-1 after:h-px after:bg-canvas after:transition-all after:duration-500 ${
                  isActive
                    ? "text-canvas after:w-full"
                    : "text-canvas/90 hover:text-canvas after:w-0 hover:after:w-full"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4 xl:gap-6 shrink-0">
          <LangToggle className="hidden md:flex" />
          <button
            type="button"
            onClick={openBookingModal}
            data-testid="nav-cta-reserve"
            className="hidden md:inline-flex items-center shrink-0 px-5 xl:px-6 py-3 text-[11px] font-medium tracking-[0.2em] uppercase border border-canvas/70 text-canvas hover:bg-canvas hover:text-brand-red transition-all duration-500 whitespace-nowrap"
          >
            {t.nav.reserve}
          </button>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-canvas shrink-0"
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-500 ease-out bg-brand-red border-t border-white/10 ${
          open ? "max-h-[80vh]" : "max-h-0"
        }`}
      >
        <nav className="px-6 py-8 flex flex-col gap-6">
          {NAV_PAGES.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              onClick={() => setOpen(false)}
              data-testid={`nav-mobile-link-${l.to === "/" ? "start" : l.to.slice(1)}`}
              className={({ isActive }) =>
                `serif text-2xl transition-colors ${
                  isActive ? "text-canvas" : "text-canvas/80 hover:text-canvas"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              openBookingModal();
            }}
            data-testid="nav-mobile-cta-reserve"
            className="serif text-2xl text-left text-canvas border-t border-white/20 pt-6 mt-2"
          >
            {t.nav.reserveArrow}
          </button>
          <LangToggle className="mt-2" />
        </nav>
      </div>
    </header>
  );
}
